import Vue from 'vue';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import moment from '@/utils/moment-wrapper.ts';
import {
    fetchAllWithProductLines,
    fetchApplications,
    fetchDeletedSubcriptions,
    fetchSubscription,
    fetchSummary,
    SuperListCacheStore,
    update,
    updateFlag,
    updateAutoGrantAccessExpiresOn,
    requestAutoAccess,
    setFlagForType,
    setProductLineMetadata,
    minimizeSubForTableAndDb,
    updatePlan,
    updateVisitorType,
    updateObjectPropertyLimit as updateObjectPropertyLimitApi,
    startFreeTrial,
} from './super.utils';
import { setSubscriptionFlags as setLocalSubscriptionFlags } from '@/data/flags/local-flags';
import { getFeatureFlag } from '@/vuex/modules/subscription/subscription.helpers.js';
import {
    SUB_PROPERTY_API_BINDING_KEYS,
    updateAutoProvFlag,
    updateByName,
} from '@/vuex/modules/subscription/subscription.utils.js';
import { APP_PLATFORM_TYPES, getAppsForPlatforms } from '@/utils/apps';
import { AppsService } from '@/common/apps/apps.service';
import { deepFreeze } from '@/utils/deep-freeze.js';

async function setUpCache (data) {
    await SuperListCacheStore.clear();
    await SuperListCacheStore.setAll(data);
}

export function getInitialState () {
    return {
        active: {},
        error: null,
        fetching: false,
        map: {},
        lastUpdatedAt: null,
        deletedMap: {},
        activeProductLinesMap: {},
    };
}

function deepFreezeSubList (subList) {
    const frozenList = subList.map((sub) => {
        return deepFreeze(sub);
    });

    // Don't freeze the array itself, so it can be sorted
    return frozenList;
}

const config = {
    namespaced: true,
    state: getInitialState(),
    mutations: {
        // active here represents the subscription a super user is viewing on the super/subs/:sid url
        // setActive is looping over entries since it looks like we're not returning `subscription.users` somewhere
        setActiveById (state, { id }) {
            // setting active from the map will drop `subscription.users`
            Object.entries(state.map[id]).forEach(([key, value]) => {
                Vue.set(state.active, key, value);
            });
            setLocalSubscriptionFlags(state.active?.featureFlags, id);
        },
        setActive (state, { subscription }) {
            state.active = subscription;

            if (subscription?.id) {
                setLocalSubscriptionFlags(subscription.featureFlags, subscription.id);
            }
        },
        setMap (state, { map }) {
            // prevent vue reactivity using Object.freeze in order to reduce memory consumption
            state.map = Object.freeze(map);
        },
        setFetching (state, fetching) {
            state.fetching = fetching;
        },
        setLastUpdatedAt (state, { lastUpdatedAt }) {
            state.lastUpdatedAt = lastUpdatedAt;
        },
        setDeletedMap (state, { map }) {
            state.deletedMap = map;
        },
        setSubscriptionUser (state, { user }) {
            const index = state.active.users.findIndex((usr) => usr.id === user.id);

            Vue.set(state.active.users, index, user);
        },
        setFlag (state, { flag, enabled }) {
            Vue.set(state.active.featureFlags, flag, enabled);
        },
        setProperty (state, { property, value }) {
            Vue.set(state.active, property, value);
        },
        setError (state, { error }) {
            state.error = error;
        },
        resetActive (state) {
            Vue.set(state, 'active', {});
            Vue.set(state, 'activeProductLinesMap', {});
        },
        reset (state) {
            Object.assign(state, getInitialState());
        },
        setActiveProductLinesMap (state, { productLinesMap }) {
            Vue.set(state, 'activeProductLinesMap', productLinesMap);
        },
        updateActiveProductLine (state, { productLine }) {
            state.activeProductLinesMap[productLine.type] = productLine;
        },
    },
    actions: {
        async updateActive ({ commit }, { id }) {
            commit('setActiveById', { id });
        },
        async loadDeletedSubs ({ state, commit }, { reFetch = false } = {}) {
            if (!isEmpty(state.deletedMap) && !reFetch) {
                return;
            }

            commit('setFetching', true);
            try {
                const deletedSubs = await fetchDeletedSubcriptions();
                if (!isNil(deletedSubs)) {
                    const { subscriptionList = [], lastUpdatedAt } = deletedSubs;
                    commit('setDeletedMap', {
                        map: Object.freeze(keyBy(subscriptionList, 'id')),
                    });
                    commit('setLastUpdatedAt', {
                        lastUpdatedAt: moment(lastUpdatedAt).format('MMM D, YYYY h:mma z'),
                    });
                }
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', false);
            }
        },
        updateMap ({ commit }, { subscriptions }) {
            commit('setMap', {
                map: keyBy(deepFreezeSubList(subscriptions), 'id'),
            });

            // update indexedDB with refreshed data but do not block UI with await
            setUpCache(subscriptions);
        },
        async loadAll ({ state, commit, dispatch }, { reFetch = false } = {}) {
            if (state.fetching || (!isEmpty(state.map) && !reFetch)) {
                return;
            }

            commit('setFetching', true);

            const blocked = await SuperListCacheStore.storeLikelyBlocked();
            if (!blocked) {
                const cachedSubscriptions = await SuperListCacheStore.getAll();
                if (!isEmpty(cachedSubscriptions)) {
                    commit('setMap', {
                        map: keyBy(deepFreezeSubList(cachedSubscriptions), 'id'),
                    });
                }
            }

            dispatch('refreshAll');
        },
        async refreshAll ({ commit, dispatch }) {
            try {
                let superSubscriptions;
                let lastUpdatedAt;
                commit('setFetching', true);

                const subListSnapshot = await fetchAllWithProductLines();

                if (!isNil(subListSnapshot)) {
                    const { subscriptionList = [], lastUpdatedAt: _lastUpdatedAt } = subListSnapshot;
                    superSubscriptions = subscriptionList.map(minimizeSubForTableAndDb);
                    lastUpdatedAt = moment(_lastUpdatedAt).format('MMM D, YYYY h:mma z');
                }

                dispatch('updateMap', { subscriptions: superSubscriptions });
                commit('setLastUpdatedAt', { lastUpdatedAt });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', false);
            }
        },
        async loadOne ({ commit }, { id, includeDeactivated }) {
            commit('setFetching', true);

            // load sub, then load supporting info if necessary
            const [subscription, apps, summary] = await Promise.all([
                fetchSubscription(id),
                fetchApplications(id, { includeDeactivated }),
                fetchSummary(id),
            ]);
            const applications = apps.filter((app) => !AppsService.isDeactivatedApp(app));
            const deactivatedApplications = apps.filter((app) => AppsService.isDeactivatedApp(app));
            const { users, permissionGroups } = summary;
            commit('setActive', {
                subscription: {
                    ...subscription,
                    applications,
                    deactivatedApplications,
                    permissionGroups,
                    users,
                },
            });

            const productLinesMap = keyBy(subscription.productLines, 'type');

            commit('setActiveProductLinesMap', { productLinesMap });
            commit('setFetching', false);
        },
        async updateProperty ({ state, commit }, { property, value }) {
            try {
                const bindingKey = SUB_PROPERTY_API_BINDING_KEYS[property];
                if (bindingKey) {
                    // Note that the non-super API is called here.
                    await updateByName(property, { [bindingKey]: value });
                } else {
                    await update({ ...state.active, [property]: value });
                }
                commit('setProperty', { property, value });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateFlag ({ commit }, { id, flag, enabled }) {
            try {
                await updateFlag({ subscriptionId: id, flag, enabled });
                commit('setFlag', { flag, enabled });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateObjectPropertyLimit ({ state, commit }, { id, count }) {
            try {
                await updateObjectPropertyLimitApi({ subscriptionId: id, count });
                if (String(state.active?.id) === String(id)) {
                    commit('setProperty', { property: 'objectPropertyLimit', value: count });
                }
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateAutoProvFlag ({ commit }, { id, reason }) {
            try {
                await updateAutoProvFlag({ subscriptionId: id, reason });
                commit('setFlag', { flag: 'autoProvisioned', enabled: false });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateAutoGrantAccessExpiresOn ({ commit }, { id, property, value }) {
            try {
                await updateAutoGrantAccessExpiresOn({ id, property, value });
                commit('setProperty', { property, value });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async requestAutoAccess ({ commit, dispatch, rootGetters }, subscription) {
            const subscriptionId = subscription?.id;
            const username = rootGetters['auth/userName']; // get username from authenticated user.

            try {
                await requestAutoAccess({ subscriptionId, username }); // request auto access.
                await dispatch('subscription/addSubToList', { subscription }, { root: true }); // add sub to user subscription list.
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async fetchSubById ({ commit }, { id }) {
            try {
                commit('setFetching', true);

                const response = await fetchSubscription(id);

                return response;
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', false);
            }
        },
        async setFlagForType ({ commit, dispatch, rootState }, { id: subscriptionId, type, flag, set }) {
            try {
                const activeSubId = get(rootState, 'subscription.active.id');
                const productLine = await setFlagForType({ type, flag, set, subscriptionId });

                // keep both modules in sync if super is editing sub they are active in
                if (activeSubId === subscriptionId) {
                    dispatch('productLines/updateMap', { productLine }, { root: true });
                }
                commit('updateActiveProductLine', { productLine });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async setProductLineMetadata ({ commit, dispatch, rootState }, { id: subscriptionId, type, data }) {
            try {
                const activeSubId = get(rootState, 'subscription.active.id');
                const productLine = await setProductLineMetadata({ type, data, subscriptionId });

                // keep both modules in sync if super is editing sub they are active in
                if (activeSubId === subscriptionId) {
                    dispatch('productLines/updateMap', { productLine }, { root: true });
                }
                commit('updateActiveProductLine', { productLine });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updatePlan (
            { commit, dispatch },
            { subscriptionId, plan, isUpgrade, sfdcAccountId, sfdcAccountName, downgradeMethod = 'immediate', query },
        ) {
            const planBody = { plan };
            if (isUpgrade) {
                if (!sfdcAccountId) {
                    throw new Error('Upgrading a plan requires a SFDC Account ID');
                }
                planBody.sfdcAccountId = sfdcAccountId;
                if (sfdcAccountName) planBody.sfdcAccountName = sfdcAccountName;
            } else {
                planBody.downgradeMethod = downgradeMethod;
            }

            try {
                const { id } = await updatePlan(subscriptionId, planBody, query);
                dispatch('loadOne', { id });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateVisitorType ({ commit, dispatch }, { subscriptionId, visitorType }) {
            try {
                await updateVisitorType(subscriptionId, visitorType);

                await dispatch('loadOne', { id: subscriptionId });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async startFreeTrial ({ commit }, { subscriptionId, duration = 14, checklist, upfront } = {}) {
            try {
                const subscription = await startFreeTrial({ subscriptionId, duration, checklist, upfront });
                commit('setActive', { subscription });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
    },
    getters: {
        list: (state) => values(state.map),
        lastUpdatedAt: (state) => state.lastUpdatedAt,
        deletedSubList: (state) => values(state.deletedMap),
        getFeatureFlag: (state) => (flag) => getFeatureFlag(state.active, flag),
        activeSubAppWebList: (state) =>
            getAppsForPlatforms(get(state, 'active.applications', []), [APP_PLATFORM_TYPES.WEB]),
        getActiveProductLines: (state) => values(state.activeProductLinesMap),
        visitorType: (state) => {
            return state.active.visitorType;
        },
    },
};

export default config;
